import React from "react";

/**
 * CareLogix™ ORACLE — OANDA-style Account Card (Phase 22).
 *
 * Mirrors the layout of the OANDA mobile screenshot:
 *   - Currency / type / Primary header chip
 *   - Account ID
 *   - NAV (big), Balance, Margin Used, Margin Available, Margin Closeout %
 *   - TRADE + ADD FUNDS (demo: ADD FUNDS opens a research note)
 *   - SIGN OUT
 *
 * Strictly read-only in DEMO mode.
 */

function money(n, ccy = "USD") {
  if (typeof n !== "number") return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${ccy} ${n.toFixed(2)}`;
  }
}

export default function OandaAccountCard({
  account,
  onTrade,
  onAddFunds,
  onSignOut,
} = {}) {
  if (!account) {
    return <div className="fx-acct fx-acct--loading" data-testid="fx-account-card-loading">Loading account…</div>;
  }
  const ccy = account.currency || "USD";
  return (
    <article className="fx-acct" data-testid="fx-account-card">
      <header className="fx-acct__head">
        <div className="fx-acct__chip">
          {ccy} · {account.type || "V20"} <span className="fx-acct__chip-tag">{account.tag || "Primary"}</span>
        </div>
        <div className="fx-acct__user">
          <strong data-testid="fx-account-name">{account.user_name || "Demo Trader"}</strong>
          <span data-testid="fx-account-email">{account.user_email || "—"}</span>
        </div>
      </header>
      <div className="fx-acct__id">
        Account: <code data-testid="fx-account-id">{account.account_id || "—"}</code>
      </div>
      <div className="fx-acct__nav" data-testid="fx-account-nav">
        <span className="fx-acct__nav-label">NAV</span>
        <span className="fx-acct__nav-value">{money(account.nav, ccy)}</span>
      </div>
      <dl className="fx-acct__grid">
        <div><dt>Balance</dt><dd data-testid="fx-account-balance">{money(account.balance, ccy)}</dd></div>
        <div><dt>Margin Used</dt><dd data-testid="fx-account-margin-used">{money(account.margin_used, ccy)}</dd></div>
        <div><dt>Margin Available</dt><dd data-testid="fx-account-margin-avail">{money(account.margin_available, ccy)}</dd></div>
        <div>
          <dt>Closeout Risk</dt>
          <dd data-testid="fx-account-closeout">
            {typeof account.margin_closeout_percent === "number"
              ? `${(account.margin_closeout_percent * 100).toFixed(2)}%`
              : "—"}
          </dd>
        </div>
      </dl>
      <div className="fx-acct__actions">
        <button type="button" className="fx-btn fx-btn--primary" onClick={onTrade} data-testid="fx-account-trade">TRADE</button>
        <button type="button" className="fx-btn" onClick={onAddFunds} data-testid="fx-account-add-funds">ADD FUNDS</button>
      </div>
      <button type="button" className="fx-acct__signout" onClick={onSignOut} data-testid="fx-account-signout">SIGN OUT</button>
      <p className="fx-acct__discl">{account.disclaimer}</p>
    </article>
  );
}
